import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { uploadFilesToImageService, MAX_FILES, MAX_FILE_SIZE } from '../../utils/uploadService';

const UploadWithImagePreviewList = ({ onFileUploadSuccess }) => {
    const [fileNames, setFileNames] = useState([]);
    const [error, setError] = useState(null);

    const ALLOWED_TYPES = [
        'image/*',
        'video/*',
        'application/pdf',
        'application/msword',
        'video/webm',
    ];

    const isValidFileType = (file) => {
        return ALLOWED_TYPES.some((type) => file.type.match(type));
    };

    const uploadFiles = async (files) => {
        if (!files.length) {
            toast.error("Please select at least one file to upload.");
            return [];
        }

        try {
            const uploadedItems = await uploadFilesToImageService(files, {
                folder: "survey-2026",
            });

            const uploadedUrls = uploadedItems
                .map((item) => item?.url)
                .filter(Boolean);

            if (!uploadedUrls.length) {
                toast.error("File upload failed. No URL found.");
                return [];
            }

            uploadedUrls.forEach((url) => {
                if (onFileUploadSuccess) {
                    onFileUploadSuccess(url);
                }
            });

            toast.success(`${uploadedUrls.length} file(s) uploaded successfully!`);
            return uploadedItems;
        } catch (error) {
            // Surface the real reason (HTTP status / network / CORS) so failures
            // are diagnosable instead of a generic message.
            toast.error(`Upload failed: ${error?.message || "network error"}`);
            console.error("Bulk upload error:", error);
            return [];
        }
    };

    const handleFileChange = async (event) => {
        setError(null);
        const files = Array.from(event.target.files || []);
        const validFiles = [];

        if (fileNames.length + files.length > MAX_FILES) {
            setError(`You can upload a maximum of ${MAX_FILES} files.`);
            event.target.value = "";
            return;
        }

        for (const file of files) {
            if (file.size > MAX_FILE_SIZE) {
                setError(`"${file.name}" exceeds ${Math.round(MAX_FILE_SIZE / (1024 * 1024))}MB file size limit.`);
                event.target.value = "";
                return;
            }

            if (!isValidFileType(file)) {
                setError(`"${file.name}" is not a supported file type.`);
                event.target.value = "";
                return;
            }

            validFiles.push(file);
        }

        const uploadedItems = await uploadFiles(validFiles);

        if (uploadedItems.length > 0) {
            setFileNames((prev) => [
                ...prev,
                ...uploadedItems.map((item) => item.originalName || item.key || item.url || "Uploaded file")
            ]);
        }

        event.target.value = "";
    };

    const removeFileName = (name) => {
        setFileNames((prev) => prev.filter((fileName) => fileName !== name));
    };

    return (
        <div className="upload-container border border-neutral-300 rounded-lg p-4 bg-white">
            <div className="upload-label">
                <label
                    htmlFor="file-upload-name"
                    className="upload-button d-flex align-items-center justify-content-center gap-2 p-3 border border-dashed border-neutral-400 rounded-lg bg-neutral-100 hover:bg-neutral-200 cursor-pointer"
                >
                    <Icon icon="solar:upload-linear" className="text-xl text-neutral-600" />
                    <span className="text-neutral-700 font-medium">Click to upload</span>
                    <input
                        type="file"
                        className="form-control"
                        id="file-upload-name"
                        multiple
                        hidden
                        onChange={handleFileChange}
                        accept="image/*, video/*, .pdf, .doc, .docx"
                    />
                </label>
            </div>

            {error && (
                <div className="error-message text-danger fw-semibold mt-3">
                    {error}
                </div>
            )}

            {fileNames.length > 0 && (
                <div className="uploaded-files mt-4">
                    <ul className="list-unstyled">
                        {fileNames.map((fileName, index) => (
                            <li
                                key={index}
                                className="uploaded-file-item d-flex align-items-center justify-content-between p-2 border border-neutral-300 rounded-lg mb-2 bg-neutral-50"
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <Icon
                                        icon="ph:link-break-light"
                                        className="text-xl text-neutral-500"
                                    />
                                    <span className="text-neutral-700">{fileName}</span>
                                </div>
                                <Icon
                                    icon="radix-icons:cross-2"
                                    className="remove-icon text-xl text-neutral-500 hover:text-danger-600 cursor-pointer"
                                    onClick={() => removeFileName(fileName)}
                                />
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default UploadWithImagePreviewList;
